import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'
import { useTransactions } from '../../contexts/TransactionsContext'

const BRAND_PURPLE = '#5B2EFF'
const BRAND_PURPLE_DARK = '#2A1B66'
const BRAND_ORANGE = '#FF8A00'
const SURFACE = 'rgba(27,16,64,0.96)'
const BORDER = '1px solid rgba(183,166,255,0.16)'

const operatorsByCountry = {
    'Sierra Leone': ['Orange Money', 'Africell', 'QCell'],
    Liberia: ['Lonestar Cell MTN', 'Orange Liberia'],
    Guinea: ['Orange Guinea', 'MTN Guinea', 'Cellcom Guinea'],
    Gambia: ['Africell Gambia', 'QCell Gambia', 'Gamcel'],
}

const currencyByCountry = {
    'Sierra Leone': 'SLE',
    Liberia: 'LRD',
    Guinea: 'GNF',
    Gambia: 'GMD',
}

const pillSx = {
    px: 1.6,
    py: 0.8,
    borderRadius: 999,
    bgcolor: 'rgba(255,255,255,0.14)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 800,
}

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 4,
        bgcolor: 'rgba(255,255,255,0.03)',
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(183,166,255,0.14)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
        '&.Mui-focused fieldset': { borderColor: BRAND_ORANGE },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.68)' },
}

const SummaryRow = ({ label, value, highlight = false }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>{label}</Typography>
        <Typography sx={{ color: highlight ? BRAND_ORANGE : '#fff', fontWeight: 900, textAlign: 'right' }}>
            {value}
        </Typography>
    </Box>
)

export default function MerchantSellAirtime() {
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const navigate = useNavigate()
    const { addTransaction } = useTransactions()

    const [country, setCountry] = useState('Sierra Leone')
    const [operator, setOperator] = useState('')
    const [phone, setPhone] = useState('')
    const [amount, setAmount] = useState('')
    const [note, setNote] = useState('')

    const operatorOptions = useMemo(
        () => operatorsByCountry[country] || operatorsByCountry['Sierra Leone'],
        [country]
    )

    useEffect(() => {
        if (!operatorOptions.includes(operator)) {
            setOperator(operatorOptions[0] || '')
        }
    }, [operator, operatorOptions])

    const currency = currencyByCountry[country] || 'SLE'
    const amountNumber = Number(amount || 0)

    const confirmSale = () => {
        if (!phone.trim() || amountNumber <= 0 || !operator) return

        const tx = addTransaction({
            type: 'merchant_payment',
            status: 'success',
            amount: amountNumber,
            fee: 0,
            total: amountNumber,
            currency,
            title: 'Payment Receipt',
            counterpart: operator,
            provider: operator,
            description: note.trim() || 'Merchant airtime sale',
            account: phone.trim(),
            createdAt: new Date().toISOString(),
        })

        navigate(`/receipt/${tx.id}`, { state: { transaction: tx } })
    }

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 30, md: 40 } }}>
                    {isFr ? 'Vendre airtime' : 'Sell Airtime'}
                </Typography>

                <Card
                    sx={{
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: `linear-gradient(135deg, ${BRAND_PURPLE_DARK} 0%, ${BRAND_PURPLE} 100%)`,
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
                            {isFr ? 'Vente quotidienne' : 'Daily resale'}
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 28, md: 34 }, mt: 1, maxWidth: 700 }}>
                            {isFr
                                ? 'Choisissez le reseau, et enregistrez une preuve claire de vente airtime.'
                                : 'Choose the network, and record a clear airtime-sale proof.'}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                            <Box sx={pillSx}>{country}</Box>
                            <Box sx={pillSx}>{isFr ? 'Vente locale' : 'Local sale'}</Box>
                            <Box sx={pillSx}>{isFr ? 'Recu pret' : 'Receipt ready'}</Box>
                            <Box sx={pillSx}>{isFr ? 'Faible data' : 'Low data'}</Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>
                    {isFr ? 'Details client' : 'Customer details'}
                </Typography>

                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                    <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
                        <Stack spacing={2}>
                            <TextField
                                select
                                label={isFr ? 'Pays' : 'Country'}
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                sx={inputSx}
                            >
                                {Object.keys(operatorsByCountry).map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label={isFr ? 'Operateur' : 'Operator'}
                                value={operator}
                                onChange={(e) => setOperator(e.target.value)}
                                sx={inputSx}
                            >
                                {operatorOptions.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                label={isFr ? 'Numero client' : 'Customer phone number'}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                sx={inputSx}
                            />

                            <TextField
                                label={`${isFr ? 'Montant airtime' : 'Airtime amount'} (${currency})`}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                sx={inputSx}
                            />

                            <TextField
                                label={isFr ? 'Note de vente (optionnel)' : 'Sale note (optional)'}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                multiline
                                minRows={3}
                                sx={inputSx}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                    <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
                        <Stack spacing={1.5}>
                            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>
                                {isFr ? 'Resume de vente' : 'Sale summary'}
                            </Typography>
                            <SummaryRow label={isFr ? 'Operateur' : 'Operator'} value={operator || '—'} />
                            <SummaryRow label={isFr ? 'Numero' : 'Phone'} value={phone.trim() || '—'} />
                            <SummaryRow
                                label={isFr ? 'Vente encaissee' : 'Sale collected'}
                                value={`${currency} ${amountNumber.toFixed(2)}`}
                                highlight
                            />
                            <SummaryRow
                                label={isFr ? 'Preuve' : 'Proof'}
                                value={isFr ? 'Recu emis apres confirmation' : 'Receipt issued after confirmation'}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                <Button
                    variant="contained"
                    disabled={!phone.trim() || amountNumber <= 0 || !operator}
                    onClick={confirmSale}
                    sx={{
                        borderRadius: 999,
                        py: 1.7,
                        textTransform: 'none',
                        fontWeight: 900,
                        bgcolor: BRAND_ORANGE,
                        color: '#0B0620',
                        '&:hover': { bgcolor: '#FFA227' },
                        '&.Mui-disabled': {
                            bgcolor: 'rgba(255,255,255,0.12)',
                            color: 'rgba(255,255,255,0.4)',
                        },
                    }}
                >
                    {isFr ? 'Confirmer la vente' : 'Confirm Sale'}
                </Button>
            </Stack>
        </Container>
    )
}
